import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faGraduationCap, faCheckCircle, faCheck, faTimes } from '@/fontawesome';
import { formatCurrencyCentValue } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import type { FeatureConfig, Course, CourseID } from '@/models';

interface EffectiveFeatureConfigCardProps {
  featureConfig: FeatureConfig;
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">
      <FontAwesomeIcon icon={faCheck} className="w-3 h-3 mr-1" />
      Aktiv
    </span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
      <FontAwesomeIcon icon={faTimes} className="w-3 h-3 mr-1" />
      Inaktiv
    </span>
  );
}

export default function EffectiveFeatureConfigCard({ featureConfig }: EffectiveFeatureConfigCardProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const response = await APIRoutes.listCourses({ limit: PAGINATION.FETCH_ALL_LIMIT });
      setCourses(response.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatLimit = (limit: number | null): string => {
    if (limit === null) {
      return 'Kein Limit';
    }
    return formatCurrencyCentValue(limit, 'USD');
  };

  const isCourseEnabled = (courseId: CourseID): boolean => {
    return featureConfig.allCourses || (featureConfig.courses[courseId] ?? false);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FontAwesomeIcon icon={faCheckCircle} className="w-6 h-6 mr-3 text-green-600 dark:text-green-400" />
          Effektive Feature-Konfiguration
        </h2>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto p-2">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Feature
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Limit (USD)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Core Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.coreRecommendations} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.coreRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Core-Tradingideen
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm ${featureConfig.coreRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    {formatLimit(featureConfig.coreRecommendationStrikeLimit)}
                  </span>
                </td>
              </tr>

              {/* Earning Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.earningRecommendations} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.earningRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Earnings-Tradingideen
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm ${featureConfig.earningRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    {formatLimit(featureConfig.earningRecommendationStrikeLimit)}
                  </span>
                </td>
              </tr>

              {/* Swing Trade Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.swingTradeRecommendations} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.swingTradeRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Swing-Tradingideen
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Startpreis-Limit</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm ${featureConfig.swingTradeRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    {formatLimit(featureConfig.swingTradeRecommendationPriceLimit)}
                  </span>
                </td>
              </tr>

              {/* Long Term Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.longTermRecommendations} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.longTermRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Langzeit-Tradingideen
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Startpreis-Limit</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm ${featureConfig.longTermRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    {formatLimit(featureConfig.longTermRecommendationPriceLimit)}
                  </span>
                </td>
              </tr>

              {/* Boost Recommendations */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.boostRecommendations} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.boostRecommendations ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Boost-Tradingideen
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Community Member */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.communityMember} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.communityMember ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Community-Mitglied
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Chatbot */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.chatbot} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.chatbot ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Chatbot
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Video Chatbot */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.videoChatbot} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.videoChatbot ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Video-Chatbot
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Journal */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.journal} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.journal ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Journal
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Daytrading */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.dayTrading} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.dayTrading ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Daytrading
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Options-Screener */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.optionScreener} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.optionScreener ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Options-Screener
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Video-Finder */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.videoSearch} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${featureConfig.videoSearch ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    Video-Finder
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* All Courses */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={featureConfig.allCourses} />
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium flex items-center ${featureConfig.allCourses ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
                    <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 mr-2" />
                    Alle Kurse
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                </td>
              </tr>

              {/* Individual Courses */}
              {courses.map(course => (
                <tr key={course.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge enabled={isCourseEnabled(course.id)} />
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm font-medium flex items-center ${
                      isCourseEnabled(course.id) ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'
                    }`}>
                      <span className="ml-8">{course.title}</span>
                      {!course.published && (
                        <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400">
                          Entwurf
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-400 dark:text-gray-600">—</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
